import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetContractorQuery, useLazyGetEnterpriseContractorsQuery } from "store/api/contractorsApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetEnterpriseQuery } from "store/api/enterpriseApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setContractor } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconEdit } from "assets/svg/icon-edit.svg"

import getErrorMessage from "utils/getErrorMessage"
import { formatPhoneNumber } from "utils/phoneNumber"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { EditContractorModal } from "modals/EditContractorModal"
import { EnterpriseAccountModal } from "modals/EnterpriseAccountModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import TableWrapper from "components/table/TableWrapper"

import { parseApiDataToContractor } from "../Contractors/parseApiDataToContractor"
import {
  convertEnterpriseContractorsToTableRows,
  getDefaultEnterpriseExpandedRows,
  getEnterpriseContractorTableHeaderCells,
} from "./enterpriseContractorsTable"
import Header from "./Header"

const toOption = (value?: number | string | null, label?: string | number | null): UITypes.Option[] =>
  value && label ? [{ value, label: String(label) }] : []

const parseApiDataToEnterpriseAccount = (
  enterprise: ApiTypes.Model.Enterprise,
  linkedContractors: ApiTypes.Model.Contractor[] = [],
): UITypes.EnterpriseAccount => ({
  id: enterprise.id,
  name: enterprise.name || "",
  address1: enterprise.address1 || "",
  address2: enterprise.address2 || "",
  country: toOption(enterprise.country?.id, enterprise.country?.name),
  zip: toOption(enterprise.zip?.id, enterprise.zip?.code),
  state: toOption(enterprise.state?.id, enterprise.state?.name),
  city: toOption(enterprise.city?.id, enterprise.city?.name),
  suburb:
    enterprise.suburb || enterprise.country?.id === 3
      ? toOption(enterprise.suburb?.id ?? enterprise.city?.id, enterprise.suburb?.name ?? enterprise.city?.name)
      : [],
  isDisable: !enterprise.is_enabled,
  linkedContractors: linkedContractors.length
    ? linkedContractors.map(contractor => ({ value: contractor.id, label: contractor.name }))
    : enterprise.contractor_ids.map(id => ({ value: id, label: String(id) })),
  email: enterprise.email || "",
  url: enterprise.url || "",
  fullName: enterprise.full_name || "",
  phone: enterprise.tel1 ? formatPhoneNumber(enterprise.tel1) : "",
  logo: enterprise.logo || enterprise.image,
})

export const ContractorsV2 = () => {
  const dispatch = useAppDispatch()
  const statusOptions = useAppSelector(state => state.tempData.contractorAccountStatusOptions) || []
  const [showEditContractorModal, setShowEditContractorModal] = useState(false)
  const [editingEnterpriseAccount, setEditingEnterpriseAccount] = useState<UITypes.EnterpriseAccount | null>(null)
  const [triggerGetEnterpriseContractors, { isFetching, data }] = useLazyGetEnterpriseContractorsQuery()
  const [triggerGetTableColumns, { data: columnsData, isFetching: isColumnsFetching }] = useLazyGetTableColumnsQuery()
  const [triggerGetContractor, { isFetching: isContractorFetching }] = useLazyGetContractorQuery()
  const [triggerGetEnterprise, { isFetching: isEnterpriseFetching }] = useLazyGetEnterpriseQuery()
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(getEnterpriseContractorTableHeaderCells(statusOptions), columnsData.data.config)
        : getEnterpriseContractorTableHeaderCells(statusOptions),
    [columnsData, statusOptions],
  )
  const defaultExpandedRows = useMemo(() => getDefaultEnterpriseExpandedRows(data?.data), [data?.data])

  const sortByTopType = useMemo(() => {
    if (Object.keys(urlQueryParams).length > 1) {
      return ""
    }

    if (urlQueryParams["top15"]) {
      return "Top 15"
    }

    if (urlQueryParams["bottom15"]) {
      return "Bottom 15"
    }

    return ""
  }, [urlQueryParams])

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetEnterpriseContractors(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getRequestParams = () => {
    if (urlQueryParams["top15"] || urlQueryParams["bottom15"]) {
      return {
        perPage: 15,
        page: 1,
        order_column: "installed",
        order_type: urlQueryParams["top15"] ? "DESC" : "ASC",
      }
    }

    return urlQueryParams
  }

  const handleClearAdditionalTags = () => {
    setUrlQueryParams({ top15: null, bottom15: null })
  }

  const getContractors = () => {
    if (Object.keys(urlQueryParams).length > 1 && (urlQueryParams["top15"] || urlQueryParams["bottom15"])) {
      handleClearAdditionalTags()
    } else {
      sendRequest(getRequestParams())
    }
  }

  const handleContractorAddEditSuccess = () => {
    setShowEditContractorModal(false)
    setEditingEnterpriseAccount(null)
    getContractors()
  }

  const onEditContractorClick = async (id: number) => {
    try {
      const res = await triggerGetContractor(`${id}`)
      if (res.data?.data) {
        dispatch(setContractor(parseApiDataToContractor(res.data.data)))
        setShowEditContractorModal(true)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onEditEnterpriseClick = async (id: number, linkedContractors: ApiTypes.Model.Contractor[] = []) => {
    try {
      const res = await triggerGetEnterprise(id)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
        return
      }

      if (res.data?.data) {
        setEditingEnterpriseAccount(parseApiDataToEnterpriseAccount(res.data.data, linkedContractors))
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    getContractors()
  }, [urlQueryParams])

  useEffect(() => {
    triggerGetTableColumns({ list_name: "admin_contractors_list" })
  }, [triggerGetTableColumns])

  return (
    <ContentWrapper title="Contractors">
      <Header
        tableHeaderCells={tableHeaderCells}
        meta={data?.meta}
        onAddContractorSuccess={handleContractorAddEditSuccess}
        renderTableControls={() => (
          <>
            <EditColumnsButton tableName="admin_contractors_list" tableHeaderCells={tableHeaderCells} />
          </>
        )}
      />
      <TableWrapper
        tableName="contractors-v2"
        headerCells={tableHeaderCells}
        columnsLoaded={!isColumnsFetching}
        data={convertEnterpriseContractorsToTableRows(data?.data)}
        isLoading={isFetching || isContractorFetching || isEnterpriseFetching}
        rowsExpanded={defaultExpandedRows}
        expandRowOnClick
        skippedTags={sortByTopType ? ["orderColumn", "account_status_id"] : ["account_status_id"]}
        clearAdditionalTags={handleClearAdditionalTags}
        additionalTags={
          sortByTopType
            ? [
                {
                  label: "Contractors",
                  values: [sortByTopType],
                },
              ]
            : []
        }
        getRowActions={rowData => {
          const item = rowData.rowSettings.item as ApiTypes.Model.EnterpriseContractorListRow | { type: "contractor" }
          const isEnterpriseRow = "type" in item && item.type === "enterprise"

          return {
            actionButtons: [
              {
                icon: <IconEdit />,
                tooltip: isEnterpriseRow ? "Edit enterprise" : "Edit contractor",
                handler: () => {
                  if (isEnterpriseRow) {
                    onEditEnterpriseClick(rowData.rowSettings.id, item.contractors)
                  } else {
                    onEditContractorClick(rowData.rowSettings.id)
                  }
                },
              },
            ],
          }
        }}
      />
      {!sortByTopType && <Pagination meta={data?.meta} />}
      {showEditContractorModal && (
        <EditContractorModal
          onCancel={() => setShowEditContractorModal(false)}
          onSuccess={handleContractorAddEditSuccess}
        />
      )}
      {editingEnterpriseAccount && (
        <EnterpriseAccountModal
          account={editingEnterpriseAccount}
          onCancel={() => setEditingEnterpriseAccount(null)}
          onSuccess={handleContractorAddEditSuccess}
        />
      )}
    </ContentWrapper>
  )
}
